import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import moment from 'moment';
import {MODES} from '../state/informeModel';
import {accountsLabel, accountsSummary, fieldsSummary, panelsSummary, periodSummary} from './saveSummaries';

export const TEMPLATE_ACTION_UPDATE = 'update';
export const TEMPLATE_ACTION_NEW = 'new';
export const PORTAL_ACTION_UPDATE = 'update';
export const PORTAL_ACTION_COPY = 'copy';

/**
 * «Guardar informe» (mockup): nombre, las dos cajas espejadas (Ajustes en rojo, «Lo que muestra
 * este informe» en violeta —la plantilla—), el checkbox contextual de la plantilla, el bloque vivo/foto y —sólo si el informe
 * está publicado— el bloque ámbar con los dos radios.
 *
 * `onConfirm({name, mode, alsoTemplate, templateAction, portalAction})`.
 */
export function SaveInformeModal({isOpen, toggle, informe, settings, template, templateModified, clientName, snapshotAt, saving, forceNew = false, onConfirm}) {
    const {t} = useTranslation();
    const [name, setName] = useState('');
    const [mode, setMode] = useState(MODES.LIVE);
    const [alsoTemplate, setAlsoTemplate] = useState(false);
    const [portalAction, setPortalAction] = useState(PORTAL_ACTION_UPDATE);

    useEffect(() => {
        if (!isOpen) return;
        setName(informe && informe.name && !forceNew ? informe.name : '');
        setMode((informe && informe.mode) || MODES.LIVE);
        setAlsoTemplate(false);
        setPortalAction(PORTAL_ACTION_UPDATE);
    }, [isOpen, informe, forceNew]);

    const hasTemplate = !!(template && template.id);
    const showUpdateTemplate = hasTemplate && templateModified;
    const showNewTemplate = !hasTemplate;
    const isPublished = !!(informe && informe.id && informe.is_published) && !forceNew;
    const canConfirm = name.trim().length > 0 && !saving;

    const confirm = () => {
        if (!canConfirm) return;
        onConfirm({
            name: name.trim(),
            mode,
            alsoTemplate,
            templateAction: showUpdateTemplate ? TEMPLATE_ACTION_UPDATE : TEMPLATE_ACTION_NEW,
            portalAction: isPublished ? portalAction : null,
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="inf-save-modal" data-testid="save-informe-modal">
            <ModalHeader toggle={toggle}>{forceNew ? t('informes.save.title_new', 'Guardar como informe nuevo') : t('informes.save.title', 'Guardar informe')}</ModalHeader>
            <ModalBody>
                <div className="modal-section inf-save">
                    <div className="form-row">
                        <label htmlFor="inf-save-name">{t('informes.save.name', 'Nombre del informe')}</label>
                        <input id="inf-save-name" type="text" className="form-control" value={name} autoFocus
                               placeholder={t('informes.save.name_placeholder', 'Ej.: Meta · Performance semanal')}
                               onChange={(e) => setName(e.target.value)}
                               onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}/>
                    </div>

                    <div className="inf-save-boxes">
                        <div className="inf-sec-box -settings">
                            <div className="inf-sec-box-h">{t('informes.settings.title', 'Ajustes')}</div>
                            <div className="inf-save-row">
                                <h5>{accountsLabel(settings, t)}</h5>
                                <p>{accountsSummary(settings, t)}</p>
                            </div>
                            <div className="inf-save-row">
                                <h5>{t('informes.save.period_row', 'Período, comparación y filtros')}</h5>
                                <p>{periodSummary(settings, t)}</p>
                            </div>
                        </div>
                        {/* «Plantilla: Sin plantilla» no se entendía (card gJUJZSmS): quien arma su
                            primer informe no sabe qué es una plantilla. La caja dice QUÉ se guarda —lo
                            que muestra el informe— y, sin plantilla, que queda sólo en este informe. */}
                        <div className="inf-sec-box -template">
                            <div className="inf-sec-box-h">
                                {t('informes.save.shows_title', 'Lo que muestra este informe')}
                                {hasTemplate && <span className="inf-sec-box-hn">{template.name}</span>}
                            </div>
                            {!hasTemplate && (
                                <p className="inf-save-note" data-testid="no-template-note">
                                    {t('informes.save.shows_no_template',
                                       'Se guarda con este informe. Si quieres reusarlo en otros, guárdalo también como plantilla.')}
                                </p>
                            )}
                            <div className="inf-save-row">
                                <h5>{t('informes.fields.title', 'Métricas y dimensiones')}</h5>
                                <p>{fieldsSummary(template, t)}</p>
                            </div>
                            <div className="inf-save-row">
                                <h5>{t('informes.save.panels_row', 'Gráficos, métricas y textos')}</h5>
                                <p>{panelsSummary(template, t)}</p>
                            </div>
                        </div>
                    </div>

                    {(showUpdateTemplate || showNewTemplate) && (
                        <label className="inf-check" data-testid="also-template">
                            <input type="checkbox" checked={alsoTemplate} onChange={(e) => setAlsoTemplate(e.target.checked)}/>
                            <span>
                                {showUpdateTemplate
                                    ? t('informes.save.also_update_template', {name: template.name, interpolation: {escapeValue: false},
                                        defaultValue: 'Actualizar también la plantilla "{{name}}" con estos cambios'})
                                    : t('informes.save.also_new_template', 'Guardar también como plantilla, para reusarla en otros informes')}
                            </span>
                        </label>
                    )}

                    <div className="inf-mode-block" data-testid="mode-block">
                        <label className="inf-radio">
                            <input type="radio" name="inf-mode" value={MODES.LIVE} checked={mode === MODES.LIVE} onChange={() => setMode(MODES.LIVE)}/>
                            <span><strong>{t('informes.mode.live', 'En vivo.')}</strong> {t('informes.mode.live_desc', 'Los datos se actualizan cada vez que alguien abre el informe.')}</span>
                        </label>
                        <label className="inf-radio">
                            <input type="radio" name="inf-mode" value={MODES.SNAPSHOT} checked={mode === MODES.SNAPSHOT} onChange={() => setMode(MODES.SNAPSHOT)}/>
                            <span><strong>{t('informes.mode.snapshot', 'Foto.')}</strong> {t('informes.mode.snapshot_desc', 'Congela los datos de hoy. El cliente ve siempre los mismos números.')}</span>
                        </label>
                        {mode === MODES.SNAPSHOT && snapshotAt && (
                            <p className="inf-mode-hint">{t('informes.mode.last_snapshot', {date: moment(snapshotAt).format('DD/MM/YYYY'), defaultValue: 'Última foto: {{date}}'})}</p>
                        )}
                    </div>

                    {isPublished && (
                        <div className="inf-portal-block" data-testid="portal-block">
                            <div className="inf-portal-head">
                                {t('informes.save.published_in_portal', {client: clientName || '', interpolation: {escapeValue: false},
                                    defaultValue: 'Este informe está publicado en el portal de {{client}}.'})}
                            </div>
                            <label className="inf-radio">
                                <input type="radio" name="inf-portal" value={PORTAL_ACTION_UPDATE} checked={portalAction === PORTAL_ACTION_UPDATE}
                                       onChange={() => setPortalAction(PORTAL_ACTION_UPDATE)}/>
                                <span><strong>{t('informes.save.portal_update', 'Actualizar el informe publicado.')}</strong> {t('informes.save.portal_update_desc', 'El cliente verá los cambios en su portal.')}</span>
                            </label>
                            <label className="inf-radio">
                                <input type="radio" name="inf-portal" value={PORTAL_ACTION_COPY} checked={portalAction === PORTAL_ACTION_COPY}
                                       onChange={() => setPortalAction(PORTAL_ACTION_COPY)}/>
                                <span><strong>{t('informes.save.portal_copy', 'Guardar como informe nuevo.')}</strong> {t('informes.save.portal_copy_desc', 'El informe publicado no cambia.')}</span>
                            </label>
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -alt -negative" onClick={toggle} disabled={saving}>{t('common.cancel', 'Cancelar')}</button>
                <button type="button" className="btn -alt" onClick={confirm} disabled={!canConfirm} data-testid="confirm-save">
                    {saving ? t('common.saving', 'Guardando…') : t('informes.save.confirm', 'Guardar informe')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
